import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTENT } from '../content';
import { getRoadmapEntries, flattenMilestones } from '../../domain/roadmap/roadmapMilestones';
import { useTokenomics } from '../useTokenomics';
import { RibbonMarquee } from '../RibbonMarquee';
import { RedesignComicViewer } from '../RedesignComicViewer';
import { RedesignNav, RedesignFooter } from '../SharedComponents';

const PosterGlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'GROBOLD';
    src: url('/assets/fonts/GROBOLD.woff2') format('woff2');
  }
  @font-face {
    font-family: 'BreakingTheComic';
    src: url('/assets/fonts/BreakingTheComic.woff2') format('woff2');
  }
  body, html {
    margin: 0;
    padding: 0;
    background-color: #3D8DF5;
    background-image: url('/assets/img/hero/bg.webp');
    background-size: cover;
    background-attachment: fixed;
    background-position: center;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #fff;
    overflow-x: hidden;
  }
  * { box-sizing: border-box; }
`;

const SectionWrapper = styled.section`
  padding: 100px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1440px;
  margin: 0 auto;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 60px 15px;
  }
`;

const PosterHeading = styled(motion.img)`
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto 40px auto;
  filter: drop-shadow(4px 4px 0 #000);
`;

const FloatingCharacter = styled(motion.img)`
  position: absolute;
  z-index: 5;
  filter: drop-shadow(6px 6px 0 #000);
`;

const PosterCard = styled(motion.div)`
  background: #fff;
  border: 6px solid #000;
  box-shadow: 12px 12px 0 #000;
  padding: 40px;
  color: #000;
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 900px;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 20px;
    border-width: 4px;
    box-shadow: 6px 6px 0 #000;
    font-size: 1rem;
  }
`;

const GridTwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  width: 100%;
  align-items: center;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled(motion.a)`
  background: #F7E03C;
  color: #000;
  font-family: 'GROBOLD', sans-serif;
  font-size: 2rem;
  padding: 20px 60px;
  border: 6px solid #000;
  box-shadow: 8px 8px 0 #000;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 15px 40px;
    border-width: 4px;
    box-shadow: 6px 6px 0 #000;
  }
`;

const CopyButton = styled(motion.div)`
  background: #fff;
  color: #000;
  border: 4px solid #000;
  padding: 15px 30px;
  font-family: 'Inter', sans-serif;
  font-weight: bold;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 6px 6px 0 #000;
  margin-top: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 20px;
    box-shadow: 4px 4px 0 #000;
  }
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  width: 100%;
  margin-top: 40px;
`;

const StatBox = styled.div`
  background: #F7E03C;
  border: 6px solid #000;
  box-shadow: 10px 10px 0 #000;
  padding: 40px;
  text-align: center;
  color: #000;
  margin-top: 40px;
  width: 100%;
  
  h3 {
    font-family: 'GROBOLD', sans-serif;
    font-size: 3rem;
    margin: 0 0 20px 0;
  }
  
  .stat {
    font-size: 2.5rem;
    font-family: 'BreakingTheComic', sans-serif;
    color: #3D8DF5;
    text-shadow: 2px 2px 0 #000;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    border-width: 4px;
    box-shadow: 6px 6px 0 #000;
    h3 { font-size: 2rem; }
    .stat { font-size: 1.8rem; }
  }
`;

const RoadmapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const RoadmapBox = styled(motion.div)`
  background: #fff;
  border: 4px solid #000;
  padding: 20px;
  color: #000;
  cursor: pointer;
  box-shadow: 6px 6px 0 #000;
  display: flex;
  flex-direction: column;
  
  h3 {
    margin: 0;
    font-family: 'GROBOLD', sans-serif;
    font-size: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .status {
    background: #F7E03C;
    border: 2px solid #000;
    padding: 4px 10px;
    font-size: 0.9rem;
  }
`;

const MemeGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  width: 100%;
`;

export default function RedesignB() {
  const roadmap = flattenMilestones(getRoadmapEntries());
  const { data: tokenomics } = useTokenomics();
  const [copied, setCopied] = useState(false);
  const [activeRoadmap, setActiveRoadmap] = useState<number | null>(null);

  const copyCA = () => {
    navigator.clipboard.writeText(CONTENT.hero.ca);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pressProps = {
    whileHover: { scale: 1.02, y: 2, boxShadow: "4px 4px 0 #000" },
    whileTap: { scale: 0.98, y: 4, boxShadow: "0px 0px 0 #000" },
    transition: { type: "spring" as const, stiffness: 400, damping: 17 }
  };
  
  const charHover = {
    whileHover: { scale: 1.1, rotate: [0, -5, 5, 0], y: -10 },
  };

  return (
    <>
      <PosterGlobalStyle />
      <RedesignNav />
      
      {/* HERO */}
      <SectionWrapper style={{ minHeight: '90vh', justifyContent: 'center' }}>
        <PosterHeading src="/assets/img/hero/title.webp" alt="ZEUS" />
        
        <FloatingCharacter 
          src="/assets/img/about/01.webp" 
          style={{ top: '15%', right: '10%', width: '30%', maxWidth: '350px', minWidth: '150px' }}
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring" as const, bounce: 0.5, delay: 0.2 }}
          {...charHover}
        />
        <FloatingCharacter 
          src="/assets/img/join/01.webp" 
          style={{ bottom: '10%', left: '5%', width: '25%', maxWidth: '300px', minWidth: '130px' }}
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: "spring" as const, bounce: 0.5, delay: 0.4 }}
          {...charHover}
        />

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10 }}>
          <ActionButton href={CONTENT.hero.buyLink} target="_blank" {...pressProps}>
            BUY ZEUS
          </ActionButton>
          
          <CopyButton onClick={copyCA} {...pressProps}>
            {copied ? "COPIED!" : `CA: ${CONTENT.hero.ca}`}
          </CopyButton>
        </div>
      </SectionWrapper>

      <RibbonMarquee />

      {/* VIDEO */}
      <SectionWrapper>
        <PosterCard style={{ padding: '20px', background: '#000' }}>
          <video 
            controls 
            poster="/assets/img/hero/bg.webp"
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
          >
            <source src="/assets/img/video/01.mp4" type="video/mp4" />
          </video>
        </PosterCard>
      </SectionWrapper>

      {/* ABOUT */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/about/title-pepesdog.webp" />
        <GridTwoCols>
          <PosterCard>
            {CONTENT.about.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </PosterCard>
          <div style={{ position: 'relative', height: '100%', minHeight: '300px' }}>
            <FloatingCharacter 
              src="/assets/img/about-cto/01.webp" 
              style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: '400px' }}
              {...charHover}
            />
          </div>
        </GridTwoCols>
      </SectionWrapper>

      <RibbonMarquee />

      {/* CTO */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/about-cto/title.webp" />
        <GridTwoCols>
          <div style={{ position: 'relative', height: '100%', minHeight: '300px' }}>
            <FloatingCharacter 
              src="/assets/img/artist/01.webp" 
              style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: '400px' }}
              {...charHover}
            />
          </div>
          <PosterCard>
            {CONTENT.aboutCto.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </PosterCard>
        </GridTwoCols>
      </SectionWrapper>

      <RibbonMarquee />

      {/* TOKENOMICS */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/tokenomics/title.webp" />
        <TokenGrid>
          {CONTENT.tokenomics.cards.map((card, i) => (
            <PosterCard as="a" href={card.link} target="_blank" key={i} style={{ textDecoration: 'none', display: 'block' }} {...pressProps}>
              <h4 style={{ fontFamily: 'GROBOLD', fontSize: '1.8rem', color: '#3D8DF5', margin: '0 0 10px 0' }}>{card.title}</h4>
              <p style={{ margin: 0 }}>{card.text}</p>
            </PosterCard>
          ))}
        </TokenGrid>
        
        {tokenomics && (
          <StatBox>
            <h3>LIVE STATS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <span style={{ fontFamily: 'GROBOLD', fontSize: '1.5rem' }}>LP LOCKED:</span> 
                <span className="stat"> {tokenomics.lpZeusPct.toFixed(1)}%</span>
              </div>
              <div>
                <span style={{ fontFamily: 'GROBOLD', fontSize: '1.5rem' }}>CIRCULATING:</span> 
                <span className="stat"> {tokenomics.circulatingPct.toFixed(1)}%</span>
              </div>
            </div>
          </StatBox>
        )}
      </SectionWrapper>

      <RibbonMarquee />

      {/* ROADMAP */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/roadmap/title.webp" />
        <RoadmapList>
          {roadmap.map((ms, i) => (
            <RoadmapBox 
              key={i} 
              onClick={() => setActiveRoadmap(activeRoadmap === i ? null : i)}
              {...pressProps}
            >
              <h3>
                {ms.title}
                <span className="status">{ms.status}</span>
              </h3>
              <AnimatePresence>
                {activeRoadmap === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px dashed #000' }}>
                      {ms.description}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </RoadmapBox>
          ))}
        </RoadmapList>
      </SectionWrapper>

      <RibbonMarquee />

      {/* ARTIST */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/artist/title.webp" />
        <PosterCard style={{ maxWidth: '100%', textAlign: 'center' }}>
          {CONTENT.artist.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          <img src="/assets/img/artist/01.webp" alt="Artist" style={{ maxWidth: '100%', marginTop: '40px', border: '4px solid #000' }} />
        </PosterCard>
      </SectionWrapper>

      <RibbonMarquee />

      {/* MEMES */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/memes/title.webp" />
        <MemeGallery>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
            <motion.img 
              key={num}
              src={`/assets/img/memes/0${num}.webp`} 
              style={{ width: '100%', border: '4px solid #000', boxShadow: '6px 6px 0 #000', borderRadius: '8px' }}
              whileHover={{ scale: 1.1, zIndex: 10, rotate: num % 2 === 0 ? 5 : -5 }}
            />
          ))}
        </MemeGallery>
      </SectionWrapper>

      <RibbonMarquee />

      {/* COMIC */}
      <SectionWrapper>
        <PosterHeading src="/assets/img/comic/title.webp" />
        <RedesignComicViewer />
      </SectionWrapper>

      <RibbonMarquee />

      {/* JOIN */}
      <SectionWrapper style={{ minHeight: '60vh', textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'GROBOLD', fontSize: '4rem', color: '#F7E03C', textShadow: '4px 4px 0 #000' }}>JOIN THE ARMY</h2>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '40px' }}>
          {Object.entries(CONTENT.social).map(([key, link]) => (
            <ActionButton 
              href={link} 
              target="_blank" 
              key={key}
              style={{ fontSize: '1.5rem', padding: '15px 30px' }}
              {...pressProps}
            >
              {key}
            </ActionButton>
          ))}
        </div>
      </SectionWrapper>
      
      <RedesignFooter />
    </>
  );
}
